"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import type { PageContent, ComponentEntry } from "@/lib/editor-types";
import type { HeaderProps } from "@/components/renderings/Header";
import type { FooterProps } from "@/components/renderings/Footer";
import Header from "@/components/renderings/Header";
import Footer from "@/components/renderings/Footer";
import Banner from "@/components/renderings/banner";
import PageSection from "@/components/renderings/PageSection";
import MosaicStatement from "@/components/renderings/MosaicStatement";

function renderComponent(entry: ComponentEntry) {
  switch (entry.type) {
    case "banner":
      return <Banner key={entry.id} {...entry.props} />;
    case "pageSection":
      return <PageSection key={entry.id} {...entry.props} />;
    case "mosaicStatement":
      return <MosaicStatement key={entry.id} {...entry.props} />;
  }
}

export function LivePageRender({ initial }: { initial: PageContent }) {
  const [header, setHeader] = useState<HeaderProps | undefined>(initial.header);
  const [footer, setFooter] = useState<FooterProps | undefined>(initial.footer);
  const [components, setComponents] = useState(initial.components);
  const containerRef = useRef<HTMLDivElement>(null);

  // Report the true content height to the parent editor.
  // The container uses min-h-screen + flex which inflates to fill the iframe
  // viewport, causing a circular dependency. We temporarily strip those
  // constraints, measure, and restore — all synchronous so no visual flicker.
  const reportHeight = useCallback(() => {
    if (window.self === window.top) return; // not in iframe
    const el = containerRef.current;
    if (!el) return;

    const mainEl = el.querySelector("main") as HTMLElement | null;

    // Collapse layout constraints
    el.style.minHeight = "0px";
    el.style.display = "block";
    if (mainEl) mainEl.style.flex = "0 0 auto";

    // Force synchronous reflow
    void el.offsetHeight;

    const h = el.scrollHeight;

    // Restore so Tailwind classes take over again
    el.style.minHeight = "";
    el.style.display = "";
    if (mainEl) mainEl.style.flex = "";

    window.parent.postMessage(
      { type: "cl-editor-height", height: h },
      window.location.origin,
    );
  }, []);

  useEffect(() => {
    // Signal to the editor that this page has hydrated and is ready to receive updates
    window.parent.postMessage(
      { type: "cl-editor-ready" },
      window.location.origin,
    );

    const handler = (e: MessageEvent) => {
      if (
        e.origin === window.location.origin &&
        e.data?.type === "cl-editor-preview"
      ) {
        const c = (e.data as { content: PageContent }).content;
        setComponents(c.components);
        setHeader(c.header);
        setFooter(c.footer);
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  // Observe own container for size changes and report to parent
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      requestAnimationFrame(reportHeight);
    });
    ro.observe(el);
    reportHeight();
    return () => ro.disconnect();
  }, [reportHeight]);

  // Re-measure after content state changes (ResizeObserver may not fire
  // when the container is locked to min-h-screen = iframe viewport)
  useEffect(() => {
    // Wait one frame for React to finish rendering the new DOM
    const id = requestAnimationFrame(reportHeight);
    return () => cancelAnimationFrame(id);
  }, [components, header, footer, reportHeight]);

  return (
    <div ref={containerRef} className="min-h-screen flex flex-col">
      <Header {...(header ?? {})} />
      <main className="flex-1">
        <div className="flex flex-col">{components.map(renderComponent)}</div>
      </main>
      <Footer {...(footer ?? {})} />
    </div>
  );
}
